import { useCallback, useEffect, useState } from 'react';

import { toast } from 'material-react-toastify';

import { hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { sessionKeys } from '@/lib/query/keys/sessions';
import { SessionStatus } from '@/lib/types/session-status';
import { isSuccess } from '@/lib/utils/response';
import { SessionHub } from '@addmile/backend-client';
import { useQueryClient } from '@tanstack/react-query';

export const useStatusChange = (sessions: SessionHub[]) => {
  const queryClient = useQueryClient();
  const [selectedStatus, setSelectedStatus] = useState<Record<string, SessionStatus>>(() => {
    const initialStatuses: Record<string, SessionStatus> = {};
    sessions.forEach((session) => {
      if (session?.id && session?.status) {
        initialStatuses[session.id] = session.status as SessionStatus;
      }
    });
    return initialStatuses;
  });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setSelectedStatus((prevStatus) => {
      const newStatuses: Record<string, SessionStatus> = {};
      let hasChanges = false;

      sessions.forEach((session) => {
        if (session?.id && session?.status && prevStatus[session.id] !== session.status) {
          newStatuses[session.id] = session.status as SessionStatus;
          hasChanges = true;
        }
      });

      return hasChanges ? { ...prevStatus, ...newStatuses } : prevStatus;
    });
  }, [sessions]);

  const handleStatusChange = useCallback(
    async (id: string, status: SessionStatus) => {
      setIsUpdating(true);
      const token = await getAuthToken();

      const statusActions = {
        [SessionStatus.Skipped]: (id: string) => hubApi.skipSessionHub(id, withAuthHeader(token)),
        [SessionStatus.Completed]: async (id: string) =>
          await hubApi.completeSessionHub(id, withAuthHeader(token)),
        [SessionStatus.Canceled]: async (id: string, coachId: string) =>
          await hubApi.cancelSessionByCoachHub(
            id,
            { coachId: coachId, cancelReason: 'Canceled by Hub' },
            withAuthHeader(token),
          ),
      };

      const changeStatusCb = statusActions[status as keyof typeof statusActions];

      if (!changeStatusCb) {
        toast.error('Status is invalid');
        setIsUpdating(false);
        return;
      }

      try {
        const coachId = sessions.find((s) => s.id === id)?.coach?.id;
        const data = await changeStatusCb(id, coachId as string);

        if (isSuccess(data.status)) {
          setSelectedStatus((prev) => ({ ...prev, [id]: status }));
          queryClient.invalidateQueries({
            queryKey: sessionKeys.detail(id),
          });
          toast.success('Status updated successfully');
        } else {
          toast.error(data.statusText);
        }
      } catch (error) {
        console.error('Error updating session status:', error);
        toast.error('Failed to update status');
      } finally {
        setIsUpdating(false);
      }
    },
    [sessions, queryClient],
  );

  return {
    handleStatusChange,
    selectedStatus,
    isUpdating,
  };
};
